import { useBookmarks } from "@circle-react-shared/Bookmarks/hooks/useBookmarks";
import { getCommentHierarchyKey } from "@circle-react/components/CommentsV3/CommentHierarchy/useCommentHierarchy";
import { chatEventsEmitter } from "@circle-react/hooks/chatsV2/chatEventsEmitter";
import { commentCache } from "@circle-react/lib/queries/comments";
import { postInvalidations } from "@circle-react/lib/queries/posts";
import type {
  Bookmark,
  BookmarkType,
  CommentBookmark,
} from "@circle-react/types/Bookmarks";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Menu } from "@circleco/compass/components/Menu";
import { useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { getCommentsListKey } from "@/react/components/CommentsV3/CommentsList/getCommentsListKey";

const isCommentBookmark = (bookmark: Bookmark): bookmark is CommentBookmark =>
  "post_id" in bookmark;

export interface ActionMenuProps {
  bookmarkId: number;
  bookmarkRecord: Bookmark | null;
  bookmarkType: BookmarkType;
  recordId: number | null;
}

export const ActionMenu = ({
  bookmarkId,
  bookmarkType,
  bookmarkRecord,
  recordId,
}: ActionMenuProps) => {
  const queryClient = useQueryClient();

  const { resetQuery, deleteBookmark } = useBookmarks({
    bookmarkId,
    bookmarkType,
    onSuccess: () => {
      if ((bookmarkType === "post" || bookmarkType === "event") && recordId) {
        void postInvalidations.postDetails(recordId);
      }
      if (
        bookmarkType === "comment" &&
        bookmarkRecord &&
        isCommentBookmark(bookmarkRecord)
      ) {
        void queryClient.refetchQueries({
          queryKey: getCommentsListKey({
            postId: bookmarkRecord.post_id,
            parentCommentId: bookmarkRecord.parent_comment_id,
          }),
        });
        void queryClient.refetchQueries({
          queryKey: getCommentHierarchyKey({
            postId: bookmarkRecord.post_id,
            id: bookmarkRecord.id,
          }),
        });
        commentCache.setBookmark({
          postId: bookmarkRecord.post_id,
          id: bookmarkRecord.id,
          bookmarkId: null,
        });
      }
      if (bookmarkType === "message" && recordId) {
        chatEventsEmitter.toggleMessageBookmark(recordId);
      }
      void resetQuery();
    },
  });

  return (
    <Menu
      align="end"
      side="bottom"
      options={[
        {
          label: t("bookmarks.actions.remove"),
          icon: "trash-can",
          danger: true,
          onClick: () => deleteBookmark(),
        },
      ]}
      trigger={
        <IconButton
          icon="dot-menu"
          size="sm"
          variant="ghost"
          aria-label={t("options_dropdown")}
        />
      }
    />
  );
};
